import getAgendaStore from '../../store/store';
import loadAgendaItemsAroundPivot from './loadAgendaItemsAroundPivot';
import { CalendarEventKey } from 'mini-calendar-event-types';
import {
    containsDateRange,
    DateRange,
    dateRangesOverlap,
    isDateInDateRange,
} from 'mini-datetime-utils';
import { getAnchorKeyInfo } from '../../utils/anchorKeyUtils';
import { userDate } from 'owa-datetime';

/**
 * Function to find if load of agenda items is needed based on eventsAddedOrRemoved prop passed.
 * @param eventsAddedOrRemoved List of events added or removed via server sent events
 */
export default function reloadAgendaItems(eventsAddedOrRemoved?: CalendarEventKey[]) {
    const { items, dateAtTop, isLoading } = getAgendaStore();
    if (items && items.length) {
        const lastItem = items[items.length - 1];
        const dateRangeOfItemsRendered: DateRange = {
            start: userDate(items[0].timestamp),
            end: userDate(lastItem.timestamp),
        };

        const { timestamp } = getAnchorKeyInfo();
        const pivot = timestamp ? userDate(timestamp) : dateAtTop;

        let isLoadAgendaItemsNeeded = false;
        if (eventsAddedOrRemoved) {
            // This check is needed as
            // If while loading spinner came in agenda view and user changed the date range in date picker
            // then the original loadAgendaItems promise will be discarded, and when the new events arrive,
            // a reload signal will be triggered and flow will come here.
            // Now when spinner would have come, then pivot will be that old dateRange
            // In this scenario, when pivot and dateRangeOfItemsRendered will not match
            // and we cannot depend to see if eventsAddedOrRemoved range falls in range of items rendered as
            // it will not be the case.
            if (!isDateInDateRange(dateRangeOfItemsRendered, pivot)) {
                isLoadAgendaItemsNeeded = true;
            } else {
                // event when we have an empty array we must load all the agendaItems
                for (const event of eventsAddedOrRemoved) {
                    const itemRange: DateRange = {
                        start: userDate(event.timestamp),
                        end: userDate(event.timestamp + 100),
                    };
                    if (
                        dateRangesOverlap(dateRangeOfItemsRendered, itemRange, true) === 0 ||
                        containsDateRange(dateRangeOfItemsRendered, itemRange, true)
                    ) {
                        // dateRangesOverlap returns zero when the ranges overlap
                        isLoadAgendaItemsNeeded = true;
                        break;
                    }
                }
            }
        } else {
            // We dont have eventsAddedOrRemoved info so we have to reload.
            // This will be the case when calendar selection/unselection is done or cache reload.
            isLoadAgendaItemsNeeded = true;
        }

        if (isLoadAgendaItemsNeeded && !isLoading) {
            loadAgendaItemsAroundPivot(pivot, !timestamp /* updateAnchorKey */);
        }
    } else {
        // This else block is needed as:
        // 1. Failure happened in first load, and then user goes in side pane and changes calendar selection.
        // In this case, loadAgendaView() is called and if else block is not there, agenda view will not load as flow will not enter if block.
        // 2. In case of any reload triggered while user is in initial failed state, again nothing will happen as no agenda items present.

        // This flow is already handled in DayView as there is no check on items present or not.
        // However adding this flow creates the problem of load agenda items around pivot being called even when reload gets triggered for every calendar addition in DAL during initial load
        // So added a check for !isLoading to proceed further.
        // Other thing to consider is dateAtTop needs to be explictly set before this code is executed.

        // Not doing a check on anchor key as if items itself are not there, anchor key info will not be there.
        if (!isLoading && dateAtTop) {
            loadAgendaItemsAroundPivot(dateAtTop);
        }
    }
}
